// ── Unified push notifications (Firebase Cloud Messaging) ─────────────────────
// One code path for web, iOS and Android via @capacitor-firebase/messaging.
// Each platform yields an FCM registration token that the backend stores and
// sends to through firebase-admin (see backend/utils/sendPush.js).

import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { isNative, isAndroid, isIOS } from './capacitor';
import { initFirebaseWeb, VAPID_KEY, isFirebaseWebConfigured } from './firebase';
import API from './api';
import safeStorage from './safeStorage';

// The exact token this device registered. Disabling has to delete the SAME
// string the backend stored, and re-deriving it at disable time is not reliable:
// on web, getToken() without the vapidKey yields a DIFFERENT token than
// getToken({ vapidKey }) did at enable time, so the delete matched nothing and
// the toggle sprang straight back on. Tokens also rotate. Remembering what we
// registered removes the guesswork; per-device storage is exactly the right
// scope for a per-device switch.
const TOKEN_KEY = 'dreamy_fcm_token';

// Reported with the token so a delivery failure can name the kind of device
// that rejected it. An APNs error means one thing for the iOS app and something
// entirely different for Safari web push, and the token alone cannot say which.
const devicePlatform = () => {
  if (!isNative()) return 'web';
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'unknown';
};

// Firebase MUST be initialised before the first call into the messaging plugin.
//
// registerPlugin() builds the web implementation lazily, on first use, and that
// constructor calls getMessaging() — which throws unless initializeApp() has
// already run. The throw happens inside the plugin's own promise chain, so it
// is swallowed: no error surfaces, and the onMessage subscription it was about
// to create never exists. Foreground messages are then delivered to nothing.
//
// That is what made a push "send successfully" and never appear: our first call
// was checkPermissions(), which ran before any initFirebaseWeb().
let webReady = false;
const ensureWebFirebase = () => {
  if (isNative() || webReady) return;
  initFirebaseWeb();
  webReady = true;
};

// Web needs a browser that supports notifications + a configured Firebase web app.
const webPushSupported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const pushSupported = () => isNative() || (webPushSupported() && isFirebaseWebConfigured());

// The current OS/browser permission, WITHOUT prompting. Asking for the state is
// not the same as asking the user, and the settings page must not fire a
// permission dialog just because it rendered.
export const getPermissionState = async () => {
  try {
    ensureWebFirebase();
    const { receive } = await FirebaseMessaging.checkPermissions();
    return receive; // 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
  } catch (_) {
    if (!isNative() && typeof Notification !== 'undefined') return Notification.permission;
    return 'prompt';
  }
};

// This device's FCM token, if one can be had without prompting. Returns null
// when permission has not been granted — deliberately, since getToken() would
// otherwise trigger the very dialog we are avoiding.
const currentToken = async () => {
  const stored = safeStorage.getItem(TOKEN_KEY);
  if (stored) return stored;
  if ((await getPermissionState()) !== 'granted') return null;
  try {
    if (isNative()) {
      const { token } = await FirebaseMessaging.getToken();
      return token || null;
    }
    initFirebaseWeb();
    let serviceWorkerRegistration;
    try {
      serviceWorkerRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    } catch (_) { /* getToken can resolve it itself */ }
    const { token } = await FirebaseMessaging.getToken({ vapidKey: VAPID_KEY, serviceWorkerRegistration });
    return token || null;
  } catch (_) {
    return null;
  }
};

/**
 * Whether THIS device is registered, plus how many others are.
 *
 * The settings screen previously derived its Enable/Disable button from the
 * account-wide `reminder.pushEnabled`, so a second device was told push was
 * "Active on this device" when it had never registered — and Disable then had
 * nothing to remove. Per-device state has to come from the device's own token.
 */
export const getDeviceStatus = async () => {
  const supported = pushSupported();
  const permission = await getPermissionState();
  if (!supported || permission === 'denied') {
    return { supported, permission, registered: false, deviceCount: 0, otherDevices: 0 };
  }
  const token = await currentToken();
  try {
    const res = await API.post('/reminders/fcm-token/status', { token: token || null });
    return {
      supported, permission,
      registered:   !!res.data?.registered,
      deviceCount:  res.data?.deviceCount ?? 0,
      otherDevices: res.data?.otherDevices ?? 0,
    };
  } catch (_) {
    return { supported, permission, registered: false, deviceCount: 0, otherDevices: 0 };
  }
};

// Request permission + obtain an FCM token + register it with the backend.
// Returns { granted, token, deviceCount } or { granted:false }.
export const enablePush = async () => {
  ensureWebFirebase();
  const perm = await FirebaseMessaging.requestPermissions();
  if (perm.receive !== 'granted') return { granted: false };

  let token;
  if (isNative()) {
    ({ token } = await FirebaseMessaging.getToken());
  } else {
    initFirebaseWeb();
    let serviceWorkerRegistration;
    try {
      serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    } catch (_) { /* getToken can still auto-register */ }
    ({ token } = await FirebaseMessaging.getToken({ vapidKey: VAPID_KEY, serviceWorkerRegistration }));
  }

  if (!token) return { granted: true, token: null };
  const res = await API.post('/reminders/fcm-token', { token, platform: devicePlatform() });
  safeStorage.setItem(TOKEN_KEY, token);
  return { granted: true, token, deviceCount: res.data?.deviceCount || 1 };
};

// Remove this device's token from the backend and delete it locally.
export const disablePush = async () => {
  ensureWebFirebase();
  // 1. What we registered, if this device still remembers it.
  let token = safeStorage.getItem(TOKEN_KEY) || null;

  // 2. Otherwise re-derive it the SAME way enablePush did — a bare getToken()
  //    on web produces a token the server never saw.
  if (!token) {
    try {
      if (isNative()) {
        ({ token } = await FirebaseMessaging.getToken());
      } else {
        ensureWebFirebase();
        let serviceWorkerRegistration;
        try {
          serviceWorkerRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        } catch (_) { /* getToken can still resolve it */ }
        ({ token } = await FirebaseMessaging.getToken({ vapidKey: VAPID_KEY, serviceWorkerRegistration }));
      }
    } catch (_) { token = null; }
  }

  if (!token) {
    // Nothing to delete server-side, and crucially nothing revoked yet — the
    // device can still identify itself on a retry.
    const err = new Error('Could not identify this device\'s notification token. Please try again, or turn notifications off in your browser or system settings.');
    err.code = 'NO_TOKEN';
    throw err;
  }

  // Tell the SERVER first, and only revoke locally once it confirms.
  //
  // This used to run the other way round: deleteToken() and the local clear
  // happened before the request. If that request then failed — a dropped
  // connection, a 500, anything — the device had already destroyed the only
  // copy of the token it was registered under. A retry minted a BRAND NEW token,
  // sent that instead, and the server rightly said "not registered". The device
  // could never disable again, while the original token stayed on the account
  // collecting notifications nobody could turn off.
  const res = await API.delete('/reminders/fcm-token', { data: { token } });
  if (res.data?.removed === false) {
    const err = new Error('This device was not registered for notifications.');
    err.code = 'NOT_REGISTERED';
    // Safe to forget it: the server has told us it is not the registered one.
    safeStorage.removeItem(TOKEN_KEY);
    throw err;
  }

  // Confirmed removed server-side — now it is safe to revoke locally.
  try { await FirebaseMessaging.deleteToken(); } catch (_) { /* already gone */ }
  safeStorage.removeItem(TOKEN_KEY);

  return { deviceCount: res.data?.deviceCount ?? 0 };
};

// Draw a notification for a message that arrived while the page was focused.
// Uses the messaging service worker's registration, because Notification API
// permission alone is not enough on mobile Chrome — it requires a service
// worker and throws "Illegal constructor" for `new Notification()`.
const showForegroundNotification = async (n) => {
  if (!n) return;
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const reg = (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js'))
             || (await navigator.serviceWorker.ready);
    if (!reg) return;
    await reg.showNotification(n.title || 'Dreamy Bedtime', {
      body: n.body || '',
      icon: n.data?.icon || '/icon-192.png',
      badge: '/icon-192.png',
      // Matches firebase-messaging-sw.js so a tap deep-links the same way.
      data: { url: n.data?.url || '/' },
      tag: 'dreamy-bedtime',      // collapse duplicates rather than stack them
      renotify: false,
    });
  } catch (_) { /* a banner in the page is the fallback */ }
};

// Android shows an FCM message in the tray only when the app is BACKGROUNDED.
// In the foreground the plugin hands it to us and draws nothing, so a test fired
// from the settings screen delivered successfully and appeared to do nothing.
// A local notification puts it in the tray immediately. iOS needs none of this —
// presentationOptions makes the system draw it — and doing both would double it.
const showAndroidForegroundNotification = async (n) => {
  if (!n) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      const asked = await LocalNotifications.requestPermissions();
      if (asked.display !== 'granted') return;
    }
    await LocalNotifications.schedule({
      notifications: [{
        // Fixed id: a second test replaces the first rather than stacking.
        id: 424242,
        title: n.title || 'Dreamy Bedtime',
        body:  n.body  || '',
        // Carried through so a tap deep-links exactly like a tray notification.
        extra: { url: n.data?.url || '/' },
        smallIcon: 'ic_stat_icon_config_sample',
      }],
    });
  } catch (_) { /* the in-app banner remains the fallback */ }
};

// Where a notification tap sends the user. Shared by the FCM tap and the
// Android local-notification tap so both behave identically.
//
// Only ever navigates WITHIN the bundled app: the native WebView serves from
// https://localhost, so assigning an absolute production URL would leave the app
// shell and load the live website inside the WebView — dropping the Capacitor
// bridge, and with it IAP and native push. Senders pass relative paths
// ('/dashboard/generate'); this makes that a guarantee, not a convention.
// '//host' and 'javascript:' are rejected too.
const navigateToPushUrl = (url) => {
  if (!url) return;
  const path = String(url);
  window.location.href = (path.startsWith('/') && !path.startsWith('//')) ? path : '/dashboard';
};

// Wire notification tap (deep-link) + optional foreground handler + token refresh.
// Returns a cleanup function.
export const initPushListeners = ({ onForeground } = {}) => {
  // Attaching a listener also constructs the web implementation, so the same
  // ordering rule applies here.
  ensureWebFirebase();
  const handles = [];
  const add = (event, cb) => { FirebaseMessaging.addListener(event, cb).then(h => handles.push(h)).catch(() => {}); };

  add('notificationActionPerformed', (event) => {
    navigateToPushUrl(event?.notification?.data?.url);
  });

  // A local notification (the Android foreground path) raises its own event, so
  // without this a tap on one simply reopened the app and went nowhere.
  if (isNative() && isAndroid()) {
    import('@capacitor/local-notifications')
      .then(({ LocalNotifications }) =>
        LocalNotifications.addListener('localNotificationActionPerformed', (e) => {
          navigateToPushUrl(e?.notification?.extra?.url);
        }).then(h => handles.push(h)))
      .catch(() => {});
  }

  // Foreground delivery shows NOTHING by default. FCM only draws a system
  // notification when the page/app is backgrounded (web via the service
  // worker's onBackgroundMessage, Android via the system tray). With the app
  // open — which is exactly the case when someone presses "Send test
  // notification" — the message arrives here and silently goes nowhere.
  // iOS is the exception: capacitor.config sets presentationOptions, so it
  // draws a banner itself; drawing another would double it.
  add('notificationReceived', (event) => {
    const n = event?.notification;
    if (onForeground) { try { onForeground(n); } catch (_) {} }
    if (!isNative())      showForegroundNotification(n);   // web: service worker
    else if (isAndroid()) showAndroidForegroundNotification(n);
    // iOS draws its own banner from FirebaseMessaging.presentationOptions.
  });
  // Token rotation — keep the backend in sync.
  add('tokenReceived', (event) => {
    if (event?.token) API.post('/reminders/fcm-token', { token: event.token, platform: devicePlatform() }).catch(() => {});
  });

  return () => handles.forEach(h => h?.remove?.());
};
